import React from 'react';
import { Link } from 'react-router-dom';

interface Testimonial {
  name: string;
  role: string;
  content: string;
  avatar: string;
}

interface StatsBannerProps {
  stats: { value: string; label: string }[];
}

interface NewsletterSectionProps {
  testimonials: Testimonial[];
  stats: StatsBannerProps;
}

export default function NewsletterSection({ testimonials, stats }: NewsletterSectionProps) {
  const [formStatus, setFormStatus] = React.useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = React.useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u065t3pcjqs2fcsjsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormError(serverMsg || '订阅失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接');
      setFormStatus('error');
    }
  };

  return (
    <section className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-16">
          {stats.stats.map((stat) => (
            <div key={stat.label} className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
              <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600 mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-foreground-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              读者口碑
            </h2>
            <p className="text-foreground-500 text-sm md:text-base">
              听听我们的读者怎么说
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <div className="text-sm font-semibold text-foreground-800">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  &ldquo;{t.content}&rdquo;
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Newsletter */}
        <div className="max-w-[600px] mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            订阅亚洲专区
          </h2>
          <p className="text-foreground-500 text-sm md:text-base mb-8">
            每周精选亚洲内容，直送您的邮箱。不错过任何精彩。
          </p>

          {formStatus === 'success' ? (
            <div className="p-6 rounded-lg bg-primary-100 border border-primary-200">
              <p className="text-primary-700 font-medium">感谢订阅！我们已经向您的邮箱发送了确认邮件。</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              data-readdy-form="true"
              id="newsletter-form"
            >
              <input
                type="email"
                name="email"
                placeholder="输入您的邮箱地址"
                required
                className="flex-1 px-4 py-3 text-sm rounded-full border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <button
                type="submit"
                className="px-6 py-3 text-sm font-semibold rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                立即订阅
              </button>
            </form>
          )}

          {formStatus === 'error' && (
            <p className="mt-3 text-sm text-red-600">{formError}</p>
          )}
        </div>
      </div>
    </section>
  );
}